/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useTranslation } from 'react-i18next';
import { useCareInstructionState } from './CareLabelProvider/CareUserInstructionStateProvider';
import { checkArray, shortenStringWithTooltip } from '../../../Components/helper';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';

function CareComments({ handleIsCompleteChange, addCareLabelComments, handleCommentAutoSave, CareAddEditAccess, handleData, isProductArchived }) {
  const reducerState = useSelector((redState) => redState);
  const { t } = useTranslation('common');
  const [activeUnderline, setActiveUnderline] = useState({});
  const navRef = useRef(null);
  const vlinksRef = useRef(null);
  const lastSavedCommentRef = useRef(null);
  const latestCommentRef = useRef(null);
  const isSavingCommentRef = useRef(false);
  const persistCommentRef = useRef(null);
  const {
    UserDetails: { isSupplierLoggedIn },
    BOMConfigDetails: { productDetails },
    CareLabelProductReducer: { comment: savedComment },
  } = reducerState;
  const { CareLabelstate, userState } = useCareInstructionState();
  const supplierCanView = productDetails?.visibleToSupplier ?? false;

  let sortedCareLabelList = [];
  if (CareLabelstate?.CareLabelList?.length > 0) {
    sortedCareLabelList = [
      ...CareLabelstate.CareLabelList.filter((ele) => !ele.forDocument), // Items where `forDocument` is false
      ...CareLabelstate.CareLabelList.filter((ele) => ele.forDocument), // Items where `forDocument` is true
    ];
  }
  useEffect(() => {
    if (vlinksRef.current && userState.tabId) {
      const activeTab = vlinksRef.current.querySelector('.active');

      if (activeTab) {
        const tabRect = activeTab.getBoundingClientRect();
        const parentRect = vlinksRef.current.getBoundingClientRect();

        setActiveUnderline({
          width: tabRect.width,
          left: tabRect.left - parentRect.left,
        });
      } else {
        setActiveUnderline({
          width: 0,
          display: 'none',
        });
      }
    }
  }, [userState.tabId, CareLabelstate?.CareLabelList]);

  /**
   * Saves the comment when it differs from the last persisted value and the user may edit it.
   * @param {string} editorComment - Current editor content.
   * @returns {Promise<void>} Resolves once any save has finished.
   */
  const persistCommentIfChanged = async (editorComment) => {
    const lastSavedComment = lastSavedCommentRef.current ?? savedComment ?? '';
    const canSave = CareAddEditAccess && !isSupplierLoggedIn && !isProductArchived && !isSavingCommentRef.current;
    if (!canSave || editorComment === lastSavedComment) {
      return;
    }
    isSavingCommentRef.current = true;
    const isSaved = await handleCommentAutoSave(editorComment);
    isSavingCommentRef.current = false;
    if (isSaved) {
      lastSavedCommentRef.current = editorComment;
    }
  };

  /**
   * Tracks the latest editor content so an unsaved edit can be flushed on unmount.
   * @param {object} _event - CKEditor change event.
   * @param {object} editor - CKEditor instance.
   * @returns {void}
   */
  const handleCommentChange = (_event, editor) => {
    const data = editor.getData();
    latestCommentRef.current = data;
    addCareLabelComments(data);
  };

  /**
   * Auto-saves the comment when the editor loses focus.
   * @param {object} _event - CKEditor blur event.
   * @param {object} editor - CKEditor instance.
   * @returns {void}
   */
  const handleCommentBlur = (_event, editor) => {
    persistCommentIfChanged(editor.getData());
  };

  useEffect(() => {
    persistCommentRef.current = persistCommentIfChanged;
  });

  useEffect(
    () => () => {
      if (latestCommentRef.current !== null) {
        persistCommentRef.current?.(latestCommentRef.current);
      }
    },
    [],
  );

  return (
    <div className="bom-list">
      <Container fluid>
        <div className="comment">
          {isSupplierLoggedIn ? (
            (supplierCanView || isProductArchived) && (
              <div className={`${isSupplierLoggedIn && 'supplier-login'} form-group mt-16`}>
                <label className="switch-text font-heavy mb-0" htmlFor="comment">
                  {t('TAKING_DATES.CARE_LABEL_COMMENT')}
                </label>
                <CKEditor
                  editor={ClassicEditor}
                  data={CareLabelstate?.comment || ''}
                  disabled
                  config={{
                    height: 150,
                    toolbar: ['bulletedList', 'numberedList', 'undo', 'redo', 'bold', 'underline'],
                  }}
                  onChange={(_event, editor) => {
                    const data = editor.getData();
                    addCareLabelComments(data);
                  }}
                />
              </div>
            )
          ) : (
            <>
              {CareAddEditAccess ? (
                <div className="d-flex align-items-center mb-16">
                  <label className="switch-text font-heavy ml-2 mb-0" htmlFor="customSwitch1">
                    {t('TAKING_DATES.CARE_USER_INSTRUCTION_COMMENT')}
                  </label>
                  <div className="ml-auto ">
                    <div className="common-checkbox bold">
                      <input
                        type="checkbox"
                        id="isComplete1"
                        name="isComplete1"
                        checked={CareLabelstate?.isComplete}
                        onChange={handleIsCompleteChange}
                        disabled={isProductArchived}
                      />
                      <label htmlFor="isComplete1" className="chklabel">
                        <span>{t('TAKING_DATES.MARK_AS_COMPLETE')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                <label className="font-heavy text-black" htmlFor="comment">
                  {t('TAKING_DATES.CARE_LABEL_COMMENTS')}
                </label>
              )}
              <div className={`${isSupplierLoggedIn && 'supplier-login'} form-group mb-16 disign-detail-commnent`}>
                <CKEditor
                  editor={ClassicEditor}
                  data={CareLabelstate?.comment}
                  disabled={(!CareAddEditAccess, isProductArchived)}
                  className="form-control"
                  config={{
                    height: 150,
                    toolbar: ['bulletedList', 'numberedList', 'undo', 'redo', 'bold', 'underline'],
                  }}
                  onChange={handleCommentChange}
                  onBlur={handleCommentBlur}
                />
              </div>
            </>
          )}
        </div>
      </Container>
      <Container className="new-tab-conatiner" fluid>
        <div className="new-tab-list">
          <div className="tab-container">
            <div>
              <nav className="greedy-nav" ref={navRef}>
                <ul className="subnav-ul visible-links" ref={vlinksRef}>
                  {isSupplierLoggedIn || !CareAddEditAccess
                    ? checkArray(sortedCareLabelList) &&
                      sortedCareLabelList?.map((ele) => {
                        return (
                          <li
                            key={ele.tabId}
                            onClick={() => handleData(ele.tabId, ele.forDocument)}
                            className={userState?.tabId === ele?.tabId ? 'active' : ''}
                          >
                            <div className=" tab-item">
                              <span className="d-flex text-bold ml-0 align-items-center">{shortenStringWithTooltip(ele.tabName, 17)}</span>
                            </div>
                          </li>
                        );
                      })
                    : checkArray(sortedCareLabelList) &&
                      sortedCareLabelList.map((ele) => {
                        return (
                          // eslint-disable-next-line react/jsx-no-useless-fragment
                          <>
                            {ele?.showTab === true && ele?.forDocument === true && (
                              <li
                                key={ele.tabId}
                                onClick={() => handleData(ele.tabId, ele.forDocument)}
                                className={userState?.tabId === ele?.tabId ? 'active' : ''}
                              >
                                <div className="tab-item">
                                  <CustomTooltip tooltipText={ele.tabName}>
                                    <span className="d-flex text-bold ml-0 align-items-center">{shortenStringWithTooltip(ele.tabName, 17)}</span>
                                  </CustomTooltip>
                                </div>
                              </li>
                            )}
                            {!ele?.forDocument && (
                              <li
                                key={ele.tabId}
                                onClick={() => handleData(ele.tabId, ele.forDocument)}
                                className={userState?.tabId === ele?.tabId ? 'active' : ''}
                              >
                                <div className="tab-item">
                                  <CustomTooltip tooltipText={ele.tabName}>
                                    <span className="d-flex text-bold ml-0 align-items-center">{shortenStringWithTooltip(ele.tabName, 17)}</span>
                                  </CustomTooltip>
                                </div>
                              </li>
                            )}
                          </>
                        );
                      })}
                </ul>
                <div className="tab-underline" style={activeUnderline} />
              </nav>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default CareComments;
